import * as React from "react";
import Icon from "../../Icon";
import styles from "./styles.module.scss";
import Boop from "src/components/Boop";
import Dialog from "src/components/Dialog";
import { motion } from "framer-motion";
import { useFloater, DeckType } from "@src/hooks/useFloater";
import { InvitationData } from "src/components/modals/SeatingReservation";
import LoginAsGuestModal from "src/components/modals/LoginAsGuestModal";
import { FloaterProps } from "@src/components/Floater";

const FloaterItem: React.FC<{
  item: DeckType;
  onOpen?: (title: string) => void;
}> = ({ item, onOpen }) => (
  <Dialog
    key={item.title}
    fillScreen={item.fillScreen}
    trigger={
      <Boop y={2}>
        <div className={styles["floater-icon"]}>
          <div className={styles["floater-icon-box"]}>
            <Icon iconName={item.icon} />
          </div>
          <p className={`${styles["floater-paragraph"]} font-ebGaramond`}>
            {item.title}
          </p>
        </div>
      </Boop>
    }
    open={item.open}
    onOpenChange={(open) => {
      if (open && onOpen) {
        onOpen(item.title);
      }
      item.setOpen(open);
    }}
  >
    {item.modal}
  </Dialog>
);

const Floater: React.FC<FloaterProps> = ({
  getEventStreamersData,
  eventId,
  slugData,
  liveSchedules,
  hasWallet,
  access_control,
  qr_code,
  accessToken,
  eventUuid,
  getAsoebiData,
  admits,
  invitation,
  isInvitedToEvent,
}) => {
  const {
    visibleItems,
    overflowItems,
    hasOverflow,
    hasItemsToShow,
    showGuestLogin,
    isExpanded,
    toggleExpanded,
    trackView,
    openGuestLogin,
    setOpenGuestLogin,
  } = useFloater({
    getEventStreamersData,
    eventId,
    slugData,
    liveSchedules,
    access_control,
    qr_code: qr_code ?? "",
    accessToken: accessToken ?? null,
    getAsoebiData,
    eventUuid,
    admits: admits ?? 1,
    invitation,
    isInvitedToEvent: isInvitedToEvent ?? false,
  });

  return hasItemsToShow || showGuestLogin ? (
    <motion.div className={styles["floater"]}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{
          opacity: 1,
          scale: 1,
          transition: {
            delay: 1.4,
            duration: 0.5,
            ease: [0.79, 0.14, -0.15, 0.86],
          },
        }}
        layout
        className={styles["floater-container"]}
      >
        <div className={styles["floater-row"]}>
          <div className={styles["floater-items"]}>
            {visibleItems.map((item) => (
              <FloaterItem key={item.title} item={item} onOpen={trackView} />
            ))}

            {hasOverflow && overflowItems.length > 0 && (
              <Boop y={2}>
                <button
                  className={styles["floater-more-btn"]}
                  onClick={toggleExpanded}
                  aria-label={
                    isExpanded ? "Show less options" : "Show more options"
                  }
                >
                  <div className={styles["floater-icon"]}>
                    <div className={styles["floater-icon-box"]}>
                      <Icon
                        iconName={isExpanded ? "chevronUp" : "chevronDown"}
                      />
                    </div>
                    <p
                      className={`${styles["floater-paragraph"]} font-ebGaramond`}
                    >
                      {isExpanded ? "Less" : "More"}
                    </p>
                  </div>
                </button>
              </Boop>
            )}
          </div>

          {showGuestLogin && (
            <>
              <div className={styles["floater-divider"]} aria-hidden />
              <button
                type="button"
                className={styles["floater-guest-btn"]}
                aria-label="Login as guest"
                data-guest-login-trigger
                onClick={() => setOpenGuestLogin(true)}
              >
                <span
                  className={`${styles["floater-guest-btn__text"]} font-ebGaramond`}
                >
                  Login as guest
                </span>
              </button>
              <Dialog
                fillScreen={false}
                open={openGuestLogin}
                onOpenChange={setOpenGuestLogin}
              >
                <LoginAsGuestModal
                  eventUuid={eventUuid}
                  setOpen={setOpenGuestLogin}
                />
              </Dialog>
            </>
          )}
        </div>

        {hasOverflow && overflowItems.length > 0 && (
          <div
            className={`${styles["floater-overflow-wrapper"]} ${
              isExpanded ? styles["floater-overflow-wrapper--expanded"] : ""
            }`}
            style={{
              gridTemplateRows: isExpanded ? "1fr" : "0fr",
            }}
          >
            <div
              className={`${styles["floater-overflow"]} ${
                isExpanded ? styles["floater-overflow--expanded"] : ""
              }`}
            >
              {overflowItems.map((item) => (
                <FloaterItem key={item.title} item={item} onOpen={trackView} />
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  ) : null;
};

export default Floater;
